import {connect} from "react-redux";
import {Formik} from "formik";
import {Button, Col, Form, FormGroup, InputGroup, Row} from "react-bootstrap";
import {AsyncTypeahead} from "react-bootstrap-typeahead";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faX} from "@fortawesome/free-solid-svg-icons";
import styles from "./Notes.module.css";
import MDEditor from "@uiw/react-md-editor";
import Switch from "react-switch";
import {useAppDispatch, useAppSelector} from "../../app/hooks";
import {
    findTagsLikeNameAsync,
    getNoteByIdAsync, getNoteFilesByIdAsync,
    saveNoteAsync
} from "./noteSlice";
import {apiRequest} from "../../authConfig";
import {useMsal} from "@azure/msal-react";
import {useEffect, useRef, useState} from "react";
import {findCustomersLikeNameAsync} from "../customers/customersSlice";
import {setActiveLink} from "../navmenu/navbarSlice";
import {useParams} from "react-router-dom";
import 'react-dropzone-uploader/dist/styles.css'
import {NoteFileView} from "../noteFile/NoteFileView";
import _ from "lodash";

export const EditNote = () => {

    const formicRef = useRef<any>(null);
    const {instance, accounts} = useMsal();
    const dispatch = useAppDispatch();

    let {id} = useParams();

    const tagLookupLoading = useAppSelector((state) => state.note.tagsLookupStatus == 'loading');
    const customerLookupLoading = useAppSelector((state) => state.customers.customerLookupStatus == 'loading');
    const tagLookup = useAppSelector((state) => state.note.tagLookup);
    const customerLookup = useAppSelector((state) => state.customers.customerLookup);
    const note = useAppSelector((state) => state.note.note);

    const [tagSelections, setTagSelections] = useState<any[]>([]);
    const [customerSelections, setCustomerSelections] = useState<any[]>([]);
    const [markdownContent, setMarkdownContent] = useState<string | undefined>('');
    const [showRenderedMarkDown, setShowRenderedMarkDown] = useState<boolean>(false);
    const [initialFormValidationErrors, setInitialFormValidationErrors] = useState<any>({});
    const [noteChanged, setNoteChanged] = useState<boolean>(false);
    const [formIsDirty, setFormIsdirty] = useState<boolean>(false);


    const [apiToken, setApiToken] = useState<string>('');

    useEffect(() => {
        if (markdownContent !== note?.content) {
            setNoteChanged(true);
            return;
        }

        if (!_.isEqual(
            note?.tags.map(t => t.name),
            tagSelections.map(t => t.name))) {
            setNoteChanged(true);
            return;
        }

        if (!_.isEqual(
            note?.customers.map(c => c.id),
            customerSelections.map(c => c.id))) {
            setNoteChanged(true);
            return;
        }

        setNoteChanged(formIsDirty);
    }, [
        markdownContent,
        tagSelections,
        customerSelections,
        formIsDirty,
        note,
        dispatch])

    useEffect(() => {
        const noteId = id ?? '';
        if (id === '') {
            return;
        }
        requestApiAccessToken().then(apiToken =>
            setApiToken(apiToken));
        

        dispatch(setActiveLink('/notes'));
        requestApiAccessToken().then(apiToken =>
            dispatch(getNoteByIdAsync({
                apiToken: apiToken,
                id: noteId
            })).then(() => {
                dispatch(getNoteFilesByIdAsync({
                    apiToken: apiToken,
                    id: noteId
                }))
            }));
    }, [dispatch]);

    useEffect(() => {
        setInitialFormValidationErrors(note?.subject ? {} : {subject: 'Required'});
        setTagSelections(note ? note.tags : []);
        setCustomerSelections(note ? note.customers : [])
        setMarkdownContent(note?.content ? note.content : '');
    }, [note]);

    useEffect(() => {
        window.addEventListener('beforeunload', alertUser)
        return () => {
            window.removeEventListener('beforeunload', alertUser)
        }
    }, [noteChanged, formIsDirty])

    const alertUser = (e: any) => {
        if (!noteChanged && !formIsDirty) {
            return;
        }

        e.preventDefault()
        e.returnValue = ''
    }

    const requestApiAccessToken = async (): Promise<string> => {
        const request = {
            ...apiRequest,
            account: accounts[0],
        };

        try {
            const response = await instance.acquireTokenSilent(request);
            return response.accessToken;
        } catch (error) {
            const response = await instance.acquireTokenSilent(request);
            return response.accessToken;
        }
    };

    const handleTagSearch = async (query: string) => {
        const apiToken = await requestApiAccessToken();
        await dispatch(findTagsLikeNameAsync({
            apiToken: apiToken,
            name: query
        }));
    };

    const shouldUpdateLookupSelection = (selection: any[]): boolean => {
        const target = selection[selection.length - 1];
        const selections = selection.filter(s => {
            return s?.name === target?.name;
        });

        return selections?.length <= 1;
    };

    const handleCustomerSearch = async (query: string) => {
        const apiToken = await requestApiAccessToken();
        await dispatch(findCustomersLikeNameAsync({apiToken: apiToken, name: query}));
    };

    const saveNote = async ({archived, subject}
                            : { archived: boolean, subject: string }) => {
        const apiToken = await requestApiAccessToken();

        const id = note?.id ? note.id : '';

        await dispatch(saveNoteAsync({
            data: {
                id: id,
                content: markdownContent,
                tags: tagSelections,
                archived: archived,
                subject: subject,
                customers: customerSelections,
                created: undefined,
                lastModified: undefined
            },
            apiToken: apiToken
        }));

        await dispatch(getNoteByIdAsync({
            apiToken: apiToken,
            id: id
        }));

        await dispatch(getNoteFilesByIdAsync({
            apiToken: apiToken,
            id: id
        }));
    };


    return (
        <Row>
            <Col xs={12}>
                <Formik
                    validateOnChange={true}
                    validateOnMount={true}
                    innerRef={formicRef}
                    enableReinitialize={true}
                    initialValues={{
                        archived: note?.archived ? note.archived : false,
                        subject: note?.subject ? note.subject : ''
                    }}
                    onSubmit={console.log}
                    validate={values => {
                        if (values.archived !== note?.archived ||
                            values.subject !== note?.subject) {
                            setFormIsdirty(true);
                        } else {
                            setFormIsdirty(false);
                        }

                        const errors: { [key: string]: any } = {};
                        if (!values.subject) {
                            errors.subject = 'Required';
                        }
                        return errors;
                    }}
                    initialErrors={initialFormValidationErrors}
                >
                    {({
                          handleChange,
                          values,
                          errors,
                          resetForm,
                          validateForm
                      }) => (
                        <Form>
                            <Row>
                                <Col className="col-12 col-lg-6 mb-2">
                                    <Form.Group>
                                        <Form.Label>Tag</Form.Label>
                                        <InputGroup>
                                            <AsyncTypeahead
                                                newSelectionPrefix="Add new tag: "
                                                filterBy={() => true}
                                                id="searchTag"
                                                labelKey="name"
                                                isLoading={tagLookupLoading}
                                                minLength={1}
                                                onSearch={handleTagSearch}
                                                options={tagLookup}
                                                placeholder="Search for tags"
                                                selected={tagSelections}
                                                onChange={(v) => {
                                                    if (shouldUpdateLookupSelection(v)) {
                                                        setTagSelections(v);
                                                    }
                                                }}
                                                multiple={true}
                                                allowNew={true}
                                            />
                                            <Button variant="secondary"
                                                    onClick={() => {
                                                        setTagSelections([]);
                                                    }}
                                            >
                                                <FontAwesomeIcon icon={faX}/>
                                            </Button>
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col className="col-12 col-lg-6 mb-2">
                                    <Form.Group>
                                        <Form.Label>Subject</Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                id="subject"
                                                name="subject"
                                                type="text"
                                                value={values.subject}
                                                onChange={handleChange}
                                                isInvalid={!!errors.subject}
                                            />
                                            <Button variant="secondary"
                                                    onClick={async () => {
                                                        await resetForm({
                                                            values: {
                                                                archived: values.archived,
                                                                subject: ''
                                                            }
                                                        });

                                                        await validateForm();
                                                    }}
                                            >
                                                <
                                                    FontAwesomeIcon
                                                    icon={faX}
                                                />
                                            </Button>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.subject}
                                            </Form.Control.Feedback>
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col className="col-12 col-lg-6 mb-2">
                                    <Form.Group>
                                        <Form.Check type="checkbox">
                                            <Form.Check.Label>Archived</Form.Check.Label>
                                            <Form.Check.Input
                                                id="archived"
                                                checked={values.archived}
                                                type="checkbox"
                                                onChange={handleChange}
                                            />
                                        </Form.Check>
                                    </Form.Group>
                                </Col>
                                <Col className="col-12 col-lg-6 mb-3">
                                    <Form.Group>
                                        <Form.Label>Customer</Form.Label>
                                        <InputGroup>
                                            <AsyncTypeahead
                                                filterBy={() => true}
                                                id="searchCustomer"
                                                labelKey="name"
                                                isLoading={customerLookupLoading}
                                                minLength={1}
                                                onSearch={handleCustomerSearch}
                                                options={customerLookup}
                                                placeholder="Search for customers"
                                                selected={customerSelections}
                                                onChange={(v) => {
                                                    if (shouldUpdateLookupSelection(v)) {
                                                        setCustomerSelections(v);
                                                    }
                                                }}
                                                multiple={true}
                                            />
                                            <Button variant="secondary"
                                                    onClick={() => {
                                                        setCustomerSelections([]);
                                                        resetForm({
                                                            values: {
                                                                archived: values.archived,
                                                                subject: values.subject
                                                            }
                                                        });
                                                    }}
                                            >
                                                <FontAwesomeIcon icon={faX}/>
                                            </Button>
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col className="col-12 mb-3">
                                    <div className={styles.right}>
                                        <FormGroup>
                                            <InputGroup>
                                                <Button
                                                    disabled={!!errors.subject || !noteChanged}
                                                    onClick={() => saveNote(values)}
                                                >
                                                    Save
                                                </Button>
                                            </InputGroup>
                                        </FormGroup>
                                    </div>
                                </Col>
                                <Col className="col-12 mb-2">
                                    <Form.Group>
                                        <InputGroup className="col-12 mb-2">
                                            <MDEditor
                                                className="col-12 mb-2"
                                                value={markdownContent}
                                                onChange={setMarkdownContent}
                                                height={600}
                                            />
                                            <FormGroup className="col-xs-12">
                                                <InputGroup>
                                                    <Switch
                                                        checked={showRenderedMarkDown}
                                                        onChange={setShowRenderedMarkDown}
                                                        onColor="#0d6efd">
                                                    </Switch>
                                                    <Form.Label className={styles.labelMargin}>Show rendered
                                                        markdown</Form.Label>
                                                </InputGroup>
                                            </FormGroup>
                                            {showRenderedMarkDown &&
                                                <MDEditor.Markdown
                                                    className="col-12 mb-2"
                                                    source={markdownContent}/>
                                            }
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Form>
                    )
                    }
                </Formik>
            </Col>
            <NoteFileView
                requestApiAccessToken={requestApiAccessToken}
                note={note}
                dispatch={dispatch}/>
        </Row>
    );
}

export default connect()(EditNote);

// https://www.npmjs.com/package/@dropzone-ui/react
// https://docs.w3cub.com/http/basics_of_http/mime_types/complete_list_of_mime_types.html
// https://react-dropzone-uploader.js.org/docs/examples
